import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from './ui/button';
import { X } from 'lucide-react';

const POLL_INTERVAL_MS = 1500;

function formatSyncSummary(summary) {
  if (!summary || typeof summary !== 'object') {
    return 'Sync complete.';
  }
  const parts = [];
  if (typeof summary.new_definitions === 'number' || typeof summary.new_values === 'number') {
    parts.push(`${summary.new_definitions ?? 0} new definitions`);
    parts.push(`${summary.new_values ?? 0} new values`);
    if (typeof summary.removed_definitions === 'number' && summary.removed_definitions > 0) {
      parts.push(`${summary.removed_definitions} removed`);
    }
  } else if (typeof summary.total_keys === 'number') {
    parts.push(`${summary.total_keys.toLocaleString()} keys synced`);
    if (summary.by_resource_type) {
      const breakdown = Object.entries(summary.by_resource_type)
        .filter(([, n]) => n > 0)
        .map(([rt, n]) => `${rt.replace('ONLINE_STORE_', '')}: ${n}`)
        .join(', ');
      if (breakdown) parts.push(`(${breakdown})`);
    }
  } else if (typeof summary.total_rows === 'number') {
    parts.push(`${summary.total_rows.toLocaleString()} rows synced`);
  } else {
    parts.push(JSON.stringify(summary));
  }
  if (typeof summary.stale_deleted === 'number' && summary.stale_deleted > 0) {
    parts.push(`${summary.stale_deleted} stale removed`);
  }
  return `Sync complete: ${parts.join(', ')}.`;
}

export default function SyncModal({
  open,
  onClose,
  endpoint = '/api/transly/metafields/sync/',
  // Optional separate URL used for the GET poll. Defaults to `endpoint` so
  // existing callers (Phase 9 / Phase 13) where one URL serves both POST trigger
  // and GET status keep working unchanged. Phase 14 splits the two
  // (POST /theme-strings/sync/ vs GET /theme-strings/sync-status/) and passes
  // both props.
  statusEndpoint,
  title = 'Syncing Metafields',
}) {
  const pollEndpoint = statusEndpoint || endpoint;
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | running | complete | error
  const [summary, setSummary] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [progress, setProgress] = useState(null);
  const hasPostedRef = useRef(false);
  const intervalRef = useRef(null);
  const logContainerRef = useRef(null);
  const prevOpenRef = useRef(false);

  // Auto-scroll log container to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(async () => {
      try {
        const res = await axios.get(pollEndpoint);
        const data = res.data;

        // Defensive: psycopg2 occasionally hands back JSONB columns as raw
        // strings on this Django setup, so even though the backend should
        // coerce, the client checks types before trusting them. A string
        // log would crash `logs.map(...)` and unmount the modal — exactly
        // the "page goes black" failure mode we hit on 2026-05-15.
        if (Array.isArray(data.log)) {
          setLogs(data.log);
        }
        setProgress(
          data.progress && typeof data.progress === 'object' && !Array.isArray(data.progress)
            ? data.progress
            : null,
        );

        if (!data.running) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;

          if (data.error) {
            setStatus('error');
            setErrorMessage(data.error);
          } else {
            setStatus('complete');
            setSummary(formatSyncSummary(data.summary));
          }
        }
      } catch (err) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setStatus('error');
        setErrorMessage(
          err.response?.data?.error || err.message || 'Unknown error'
        );
      }
    }, POLL_INTERVAL_MS);
  }, [pollEndpoint]);

  const startSync = useCallback(async () => {
    setStatus('running');
    setLogs([]);
    setSummary('');
    setErrorMessage('');
    setProgress(null);

    try {
      await axios.post(endpoint);
      hasPostedRef.current = true;
      startPolling();
    } catch (err) {
      if (err.response?.status === 409) {
        // Sync already in progress -- start polling to follow it
        hasPostedRef.current = true;
        setLogs((prev) => [...prev, 'Sync already in progress, following...']);
        startPolling();
      } else {
        setStatus('error');
        setErrorMessage(
          err.response?.data?.error || err.message || 'Failed to start sync'
        );
      }
    }
  }, [startPolling, endpoint]);

  // Detect open transition (false -> true) to reset POST guard and start sync
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // New open cycle: re-arm the post guard and kick off a fresh sync.
      // (WR-06: removed redundant `if (!hasPostedRef.current)` check —
      // the ref was just set to false on the line above, so the inner
      // condition was always true.)
      hasPostedRef.current = false;
      startSync();
    }
    prevOpenRef.current = open;
  }, [open, startSync]);

  const handleRetry = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    hasPostedRef.current = false;
    startSync();
  }, [startSync]);

  const handleClose = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setStatus('idle');
    setLogs([]);
    setSummary('');
    setErrorMessage('');
    onClose();
  }, [onClose]);

  if (!open) return null;

  const isRunning = status === 'running';
  const isComplete = status === 'complete';
  const isError = status === 'error';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="bg-card rounded-lg shadow-lg w-full max-w-[640px] mx-4 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-base font-medium text-foreground">
            {title}
          </h2>
          {!isRunning && (
            <button
              onClick={handleClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Progress bar (rendered while running and a phase has been reported).
            When the backend reports `total`, the bar is determinate; when only
            `current` is known (mid-walk, Shopify pagination has no total
            upfront), the bar is indeterminate. */}
        {isRunning && progress?.phase && (
          <div className="mx-6 mt-4 mb-2">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>
                {progress.phase}
                {progress.detail ? ` — ${progress.detail}` : ''}
              </span>
              {typeof progress.current === 'number' && progress.total ? (
                <span>{progress.current}/{progress.total}</span>
              ) : typeof progress.current === 'number' ? (
                <span>{progress.current.toLocaleString()} walked</span>
              ) : null}
            </div>
            <div className="h-1.5 w-full bg-muted rounded overflow-hidden">
              {progress.total ? (
                <div
                  className="h-full bg-primary transition-all"
                  style={{
                    width: `${Math.min(100, Math.round((progress.current / progress.total) * 100))}%`,
                  }}
                />
              ) : (
                <div className="h-full bg-primary/40 animate-pulse" style={{ width: '40%' }} />
              )}
            </div>
          </div>
        )}

        {/* Log area */}
        <div
          ref={logContainerRef}
          className="px-6 py-4 h-[400px] overflow-y-auto bg-muted rounded-md mx-6 my-4"
        >
          {logs.map((line, i) => (
            <div key={i} className="text-xs font-mono text-foreground">
              {line}
            </div>
          ))}
          {isComplete && (
            <div className="text-xs font-mono text-green-500 mt-2">
              {summary}
            </div>
          )}
          {isError && (
            <div className="text-xs font-mono text-destructive mt-2">
              Sync failed: {errorMessage}. Click Retry Sync to try again.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
          {isError && (
            <Button variant="outline" onClick={handleRetry}>
              Retry Sync
            </Button>
          )}
          {!isRunning && (
            <Button onClick={handleClose}>Close</Button>
          )}
        </div>
      </div>
    </div>
  );
}
